/**
 * Gerar pinout gráfico — o desenho do componente, sempre gerado na hora.
 *
 * É a tela de quem está editando. A pessoa mexe no pinout, vem aqui, confere o
 * que mudou, volta. Por isso **nada é reaproveitado**: cada "Gerar" refaz o
 * desenho. Guardar responderia rápido e mostraria o estado anterior, e a
 * conferência passaria batido — que é justamente o que esta tela existe para
 * impedir.
 *
 * A consequência é assumida: **gerar demora**. Medido no projeto 976, o ECM com
 * 168 pinouts leva 18 segundos. Uma tela que demora dezoito segundos precisa
 * fazer três coisas que uma tela rápida não precisa:
 *
 *  1. **só disparar quando pedirem** — nada de gerar ao trocar de componente;
 *  2. **contar o tempo em voz alta** — 18 s de silêncio parecem travamento;
 *  3. **deixar desistir** — escolheu o componente errado, cancela e refaz.
 *
 * **O desenho é clicável**, como no Sistema de hoje: clicar num componente abre
 * o painel à direita e dali se navega — "gerar o pinout a partir deste
 * componente" redesenha centrado nele. É assim que se anda pelo circuito.
 *
 * Isso obriga o SVG a entrar no DOM: uma imagem não expõe o que tem dentro. E
 * entrar no DOM obriga a higienizar antes (ver `svgSeguro.ts`) — o motor que
 * gera o desenho concatena texto do banco sem escapar, e descrição de
 * componente é campo livre.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { usarComponentes, usarModelos } from "@/api/catalogos"
import {
  baixarImagem, baixarSvg, gerarSvg, nomeDoArquivo,
  OPCOES_PADRAO, TEXTO_CIRCUITO, TEXTO_COMPONENTE, TEXTO_CONECTOR,
  type DesenhoPronto, type OpcoesGrafico, type PedidoGrafico,
} from "@/api/grafico"
import { ErroDaApi } from "@/api/erros"
import { obter } from "@/api/cliente"
import type { PinoutResponse } from "@/api/tipos"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Baixar, DesenhoSvg, Trabalhando } from "@/kit/DesenhoSvg"
import { PainelDoComponente, type AlvoDoPainel } from "./PainelDoComponente"
import { GavetaDeCadastro, type AlvoDeEdicao } from "./GavetaDeCadastro"
import { EditorDeLigacoes } from "./editor/EditorDeLigacoes"
import { usarBuscaDeComponentes } from "@/api/editorDeLigacoes"
import type { ComponenteResponse } from "@/api/tipos"
import { Lancamento } from "../lancamento/Lancamento"
import { PublicacaoDoPinout, usarResumoDaPublicacao } from "./PublicacaoDoPinout"
import { CampoDeBusca, idDe, textoDe, VAZIO, type ValorBusca } from "@/kit/CampoDeBusca"

const CHAVE_OPCOES = "sistema-oc.grafico.opcoes"

function lerOpcoes(): OpcoesGrafico {
  try {
    const g = localStorage.getItem(CHAVE_OPCOES)
    return g ? { ...OPCOES_PADRAO, ...JSON.parse(g) } : OPCOES_PADRAO
  } catch {
    return OPCOES_PADRAO
  }
}

export function PinoutGrafico() {
  const { projetoId } = useParams()
  const id = Number(projetoId)
  const [busca] = useSearchParams()

  // Vindo da grade: "ver gráfico das linhas marcadas". O componente também vem
  // na URL, porque um desenho é sempre de um componente só.
  const pinoutsDaGrade = useMemo(() => {
    const p = busca.get("pinouts")
    return p ? p.split(",").map(Number).filter((n) => n > 0) : []
  }, [busca])

  const componentes = usarComponentes(id)
  const modelos = usarModelos(id)

  const [componente, setComponente] = useState<ValorBusca>(VAZIO)
  const [modeloId, setModeloId] = useState(0)
  const [opcoes, setOpcoes] = useState<OpcoesGrafico>(lerOpcoes)
  const [detalhes, setDetalhes] = useState(false)
  const [publicando, setPublicando] = useState(false)
  const publicacao = usarResumoDaPublicacao(id)

  const [desenho, setDesenho] = useState<DesenhoPronto | null>(null)
  const [gerando, setGerando] = useState(false)
  const [decorrido, setDecorrido] = useState(0)
  const [erro, setErro] = useState<unknown>(null)
  const [zoom, setZoom] = useState(1)
  const [alvo, setAlvo] = useState<AlvoDoPainel | null>(null)
  const [editandoCadastro, setEditandoCadastro] = useState<{ alvo: AlvoDeEdicao; contexto?: string } | null>(null)
  const [editandoPinout, setEditandoPinout] = useState<PinoutResponse | null>(null)
  /**
   * O desenho na tela virou passado — alguém salvou uma alteração depois de ele
   * ser gerado. A tela AVISA e oferece o botão; não regera sozinha. Regerar
   * sozinha custaria 18 segundos que ninguém pediu, e decidir por conta própria
   * quando o desenho está velho é exatamente o que a regra de não ter cache
   * existe para impedir.
   */
  const [envelheceu, setEnvelheceu] = useState(false)
  /**
   * O editor de ligações no lugar do desenho. Usa o mesmo componente e o mesmo
   * modelo escolhidos aqui em cima — e o mesmo "simplificar aterramento", senão
   * o que ele mostra e o que o desenho mostra seriam coisas diferentes.
   */
  const [editandoLigacoes, setEditandoLigacoes] = useState(false)
  // Vindo do botão "Editar ligações" da grade: o editor abre sozinho assim que houver componente e modelo.
  const [abrirEditor, setAbrirEditor] = useState(() => busca.get("ligacoes") === "1")
  // Catálogo maior que a página de 200 (75 projetos em 26/09/2026): o componente é buscado no servidor.
  const buscaDeComponentes = usarBuscaDeComponentes(componentes.completo ? 0 : id)
  const cancelador = useRef<AbortController | null>(null)

  // O componente pode vir da grade pela URL; o catálogo chega depois dela.
  useEffect(() => {
    const vindo = Number(busca.get("componenteId"))
    if (!vindo || componente.estado !== "vazio" || componentes.carregando) return
    const achado = componentes.itens.find((c) => c.id === vindo)
    if (achado) { setComponente({ estado: "vinculado", id: achado.id, texto: achado.codigo || achado.rotulo }); return }
    // Fora da primeira página do catálogo: pelo id, que é o que a grade mandou.
    let vivo = true
    obter<ComponenteResponse>(`api/componentes/${vindo}`, {})
      .then((c) => { if (vivo) setComponente({ estado: "vinculado", id: c.id, texto: c.codigo || c.descricao }) })
      .catch(() => { /* componente apagado entre a grade e o clique */ })
    return () => { vivo = false }
  }, [busca, componentes.itens, componentes.carregando, componente.estado])

  useEffect(() => {
    if (!abrirEditor || idDe(componente) <= 0 || modeloId <= 0) return
    setEditandoLigacoes(true)
    setAbrirEditor(false)
  }, [abrirEditor, componente, modeloId])

  // Um projeto costuma ter poucos modelos; o primeiro já vem escolhido para a
  // tela não abrir pedindo duas decisões antes de mostrar qualquer coisa.
  useEffect(() => {
    if (modeloId === 0 && modelos.itens.length > 0) setModeloId(modelos.itens[0]!.id)
  }, [modelos.itens, modeloId])

  useEffect(() => {
    try { localStorage.setItem(CHAVE_OPCOES, JSON.stringify(opcoes)) } catch { /* sem armazenamento */ }
  }, [opcoes])

  // O cronômetro. Só existe enquanto está gerando.
  useEffect(() => {
    if (!gerando) return
    const t = setInterval(() => setDecorrido((s) => s + 0.1), 100)
    return () => clearInterval(t)
  }, [gerando])

  const componenteId = idDe(componente)
  const modelo = modelos.itens.find((m) => m.id === modeloId)
  const podeGerar = componenteId > 0 && modeloId > 0 && !gerando

  const pedido: PedidoGrafico = {
    projetoId: id,
    componenteId,
    modeloId,
    pinouts: pinoutsDaGrade.length > 0 ? pinoutsDaGrade : undefined,
    opcoes,
  }

  async function gerar(componenteSobreposto?: number) {
    const alvoId = componenteSobreposto ?? componenteId
    if (alvoId <= 0 || modeloId <= 0 || gerando) return
    cancelador.current?.abort()
    const c = new AbortController()
    cancelador.current = c

    setGerando(true)
    setDecorrido(0)
    setErro(null)
    try {
      setDesenho(await gerarSvg({ ...pedido, componenteId: alvoId }, c.signal))
      setZoom(1)
      // O painel falava de um componente do desenho anterior.
      setAlvo(null)
      setEditandoCadastro(null)
      setEnvelheceu(false)
    } catch (e) {
      // Cancelar não é erro: a pessoa mandou parar.
      if (!c.signal.aborted) setErro(e)
    } finally {
      if (cancelador.current === c) { setGerando(false); cancelador.current = null }
    }
  }

  /**
   * O `Apartir()` do legado: troca o componente e redesenha. Não é atalho de
   * navegação — é como a pessoa percorre o circuito, saltando de componente em
   * componente pelas ligações que o desenho mostra.
   */
  function gerarAPartirDe(novoComponenteId: number) {
    const achado = componentes.itens.find((x) => x.id === novoComponenteId)
    setComponente(achado
      ? { estado: "vinculado", id: achado.id, texto: achado.codigo || achado.rotulo }
      : { estado: "vinculado", id: novoComponenteId, texto: String(novoComponenteId) })
    setAlvo(null)
    // Vai com o id na mão em vez de esperar o estado assentar: `setComponente`
    // só vale no próximo render, e aqui o desenho tem de sair agora.
    void gerar(novoComponenteId)
  }

  function cancelar() {
    cancelador.current?.abort()
    cancelador.current = null
    setGerando(false)
  }

  useEffect(() => () => cancelador.current?.abort(), [])

  const nome = nomeDoArquivo(id, textoDe(componente) || String(componenteId), modelo?.rotulo ?? "")

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        className="barra"
        style={{
          flex: "0 0 auto", display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 10,
          padding: "10px 18px", background: "var(--surface2)", borderBottom: "1px solid var(--line2)",
        }}
      >
        <Campo rotulo="Componente" largura={260}>
          <CampoDeBusca
            rotulo="Componente"
            valor={componente}
            aoMudar={setComponente}
            fonte={componentes.completo
              ? { modo: "memoria", itens: componentes.itens, carregando: componentes.carregando }
              : { modo: "servidor", itens: buscaDeComponentes.itens, carregando: buscaDeComponentes.carregando, aoBuscar: buscaDeComponentes.buscar }}
            permiteCriar={false}
            mono
            dica="código ou descrição"
            id="grafico-componente"
          />
          {abrirEditor && componenteId <= 0 && (
            <span style={{ fontSize: 11, color: "var(--acao-on-soft)" }}>
              Escolha o componente: o editor de ligações abre em seguida.
            </span>
          )}
        </Campo>

        <Campo rotulo="Modelo" largura={300}>
          <select
            className="campo"
            value={modeloId}
            onChange={(ev) => setModeloId(Number(ev.target.value))}
            aria-label="Modelo"
            style={{
              width: "100%", padding: "0 9px", borderRadius: 7,
              border: "1px solid var(--line2)", background: "var(--surface)", fontSize: 12.5,
            }}
          >
            {modelos.itens.length === 0 && <option value={0}>Sem modelo neste projeto</option>}
            {modelos.itens.map((m) => <option key={m.id} value={m.id}>{m.rotulo}</option>)}
          </select>
        </Campo>

        <button
          onClick={() => (gerando ? cancelar() : void gerar())}
          disabled={!gerando && !podeGerar}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 14px",
            borderRadius: 7, fontSize: 12.5, fontWeight: 600,
            background: gerando ? "var(--surface)" : "var(--brand)",
            color: gerando ? "var(--txt2)" : "#FFFFFF",
            border: gerando ? "1px solid var(--line2)" : undefined,
            opacity: !gerando && !podeGerar ? 0.45 : 1,
          }}
        >
          {gerando ? `Cancelar · ${decorrido.toFixed(1)} s` : desenho ? "Gerar de novo" : "Gerar desenho"}
        </button>

        <button
          onClick={() => setDetalhes((d) => !d)}
          aria-expanded={detalhes}
          style={{ height: 32, padding: "0 10px", fontSize: 12.5, color: "var(--txt2)" }}
        >
          Mais detalhes {detalhes ? "▴" : "▾"}
        </button>

        {/* Publicar o pinout gráfico: a produção não achava onde (relatório de 28/09/2026). */}
        <button
          onClick={() => setPublicando((v) => !v)}
          aria-expanded={publicando}
          style={{
            height: 32, padding: "0 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
            border: "1px solid var(--line2)", background: publicando ? "var(--brand-soft)" : "var(--surface)",
            color: "var(--txt)",
          }}
        >
          Publicar{" "}
          {!publicacao.carregando && publicacao.total > 0 && (
            <span style={{ fontWeight: 400, color: "var(--txt3)" }}>
              · no ar em {publicacao.noAr} de {publicacao.total}
            </span>
          )}
          {" "}{publicando ? "▴" : "▾"}
        </button>

        <button
          onClick={() => setEditandoLigacoes((e) => !e)}
          disabled={!editandoLigacoes && (componenteId <= 0 || modeloId <= 0)}
          aria-pressed={editandoLigacoes}
          title="Ligar pinos e marcar junções apontando no desenho, em vez de digitar o nome dos circuitos"
          style={{
            height: 32, padding: "0 12px", borderRadius: 7, fontSize: 12.5, fontWeight: 600,
            border: "1px solid var(--brand)",
            background: editandoLigacoes ? "var(--brand)" : "var(--surface)",
            color: editandoLigacoes ? "#FFFFFF" : "var(--brand)",
            opacity: !editandoLigacoes && (componenteId <= 0 || modeloId <= 0) ? 0.45 : 1,
          }}
        >
          {editandoLigacoes ? "Voltar ao desenho" : "Editar ligações"}
        </button>

        {envelheceu && !gerando && (
          <span
            title="Algo mudou depois que este desenho foi gerado."
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 26, padding: "0 10px",
              borderRadius: 999, background: "var(--acao-soft)", color: "var(--acao-on-soft)",
              fontSize: 11.5, fontWeight: 600,
            }}
          >
            o desenho é anterior à sua alteração
          </span>
        )}

        {pinoutsDaGrade.length > 0 && (
          <span
            title="O desenho sai só com as linhas que estavam marcadas na grade."
            style={{
              display: "inline-flex", alignItems: "center", height: 24, padding: "0 9px",
              borderRadius: 999, background: "var(--brand-soft)", color: "var(--brand-on-soft)",
              fontSize: 11.5, fontWeight: 600,
            }}
          >
            {pinoutsDaGrade.length} linha{pinoutsDaGrade.length > 1 ? "s" : ""} da grade
          </span>
        )}
      </div>

      {detalhes && (
        <div
          className="barra"
          style={{
            flex: "0 0 auto", display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: 10,
            padding: "10px 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
          }}
        >
          <Lista rotulo="Texto do componente" valor={opcoes.textoComponente} opcoes={TEXTO_COMPONENTE}
                 aoMudar={(v) => setOpcoes({ ...opcoes, textoComponente: v })} largura={190} />
          <Lista rotulo="Texto do conector" valor={opcoes.textoConector} opcoes={TEXTO_CONECTOR}
                 aoMudar={(v) => setOpcoes({ ...opcoes, textoConector: v })} largura={150} />
          <Lista rotulo="Texto do circuito" valor={opcoes.textoCircuito} opcoes={TEXTO_CIRCUITO}
                 aoMudar={(v) => setOpcoes({ ...opcoes, textoCircuito: v })} largura={210} />

          <Marca rotulo="Ordem do diagrama" ligado={opcoes.ordemDiagrama}
                 aoMudar={(v) => setOpcoes({ ...opcoes, ordemDiagrama: v })} />
          <Marca rotulo="Simplificar aterramento" ligado={opcoes.simplificarAterramento}
                 aoMudar={(v) => setOpcoes({ ...opcoes, simplificarAterramento: v })} />

          <button
            onClick={() => setOpcoes(OPCOES_PADRAO)}
            style={{ height: 30, padding: "0 10px", fontSize: 12, color: "var(--txt3)" }}
          >
            Voltar ao padrão
          </button>

          <span style={{ flex: 1, minWidth: 0 }} />
          <span style={{ fontSize: 11, color: "var(--txt3)", maxWidth: "46ch", lineHeight: 1.4 }}>
            Fica guardado para a próxima vez. Mudar aqui não redesenha — aperte Gerar.
          </span>
        </div>
      )}

      {publicando && (
        <PublicacaoDoPinout projetoId={id} componenteId={componenteId} componente={textoDe(componente)} />
      )}

      {/* O lançamento entra como faixa sobre o desenho — o mesmo lugar que ele
          ocupa sobre a grade. Editar o pinout também não tira ninguém daqui. */}
      {editandoPinout && (
        <Lancamento
          projetoId={id}
          pinout={editandoPinout}
          aoGravar={() => setEnvelheceu(true)}
          aoFechar={() => setEditandoPinout(null)}
        />
      )}

      {editandoLigacoes ? (
        <EditorDeLigacoes
          // Outro componente, modelo ou aterramento é outro grafo: a tela nasce de novo, com o
          // arranjo das caixas e a fila de alterações daquele componente.
          key={`${componenteId}-${modeloId}-${opcoes.simplificarAterramento}`}
          projetoId={id}
          componenteId={componenteId}
          modeloId={modeloId}
          simplificarAterramento={opcoes.simplificarAterramento}
          aoFechar={() => setEditandoLigacoes(false)}
          aoAlterou={() => setEnvelheceu(true)}
        />
      ) : (
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
      <div
        style={{
          flex: 1, minWidth: 0, overflow: "auto", background: "var(--surface3)",
          display: "flex", alignItems: gerando || !desenho ? "center" : "flex-start",
          justifyContent: "center", padding: 18,
        }}
      >
        {gerando ? (
          <Trabalhando segundos={decorrido} />
        ) : erro ? (
          <Aviso>
            <b style={{ color: "var(--erro)" }}>
              {erro instanceof ErroDaApi ? erro.message : "Não foi possível gerar o desenho."}
            </b>
            <span style={{ fontSize: 12, color: "var(--txt3)", maxWidth: "48ch" }}>
              Componente sem pinout no modelo escolhido costuma dar isso. Confira o modelo e tente de novo.
            </span>
          </Aviso>
        ) : desenho ? (
          <DesenhoSvg
            svg={desenho.svg}
            largura={desenho.largura}
            zoom={zoom}
            rotulo={`Pinout gráfico — ${nome}`}
            aoClicar={(marcador) => {
              const principal = marcador.classList.contains("SvgComponentePrincipal")
              setAlvo({
                // O marcador do principal é um <rect> sem `name`; o componente
                // atual é quem o identifica.
                pinoutId: principal ? 0 : Number(marcador.getAttribute("name") ?? 0),
                componenteId: principal ? componenteId : 0,
                principal,
              })
            }}
          />
        ) : (
          <Aviso>
            Escolha o componente e aperte <b>Gerar desenho</b>.
            <span style={{ fontSize: 12, color: "var(--txt3)", maxWidth: "52ch" }}>
              O desenho é sempre refeito na hora, nunca reaproveitado — é assim que ele mostra o
              pinout como está agora, e não como estava. Componente grande leva alguns segundos.
            </span>
          </Aviso>
        )}
      </div>

      {editandoCadastro ? (
        <GavetaDeCadastro
          projetoId={id}
          alvo={editandoCadastro.alvo}
          contexto={editandoCadastro.contexto}
          aoFechar={() => setEditandoCadastro(null)}
          aoSalvou={() => setEnvelheceu(true)}
        />
      ) : alvo && (
        <PainelDoComponente
          alvo={alvo}
          aoFechar={() => setAlvo(null)}
          aoGerarAPartirDe={gerarAPartirDe}
          aoEditarCadastro={(a, contexto) => setEditandoCadastro({ alvo: a, contexto })}
          aoEditarPinout={(pinoutId) => {
            obter<PinoutResponse>(`api/pinouts/${pinoutId}`, {})
              .then(setEditandoPinout)
              .catch(() => { /* linha apagada entre o desenho e o clique */ })
          }}
        />
      )}
      </div>
      )}

      <BarraDeEstado
        direita={
          desenho && !gerando ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Passo rotulo="Diminuir o desenho" aoIr={() => setZoom((z) => Math.max(0.25, z - 0.25))} texto="−" />
              <span className="num" style={{ minWidth: 44, textAlign: "center" }}>{Math.round(zoom * 100)}%</span>
              <Passo rotulo="Aumentar o desenho" aoIr={() => setZoom((z) => Math.min(4, z + 0.25))} texto="+" />
              <span style={{ width: 8 }} />
              <Baixar rotulo="SVG" aoBaixar={() => baixarSvg(desenho.svg, nome)} />
              <Baixar rotulo="PNG" aoBaixar={() => void baixarImagem(pedido, "png", nome)} />
              <Baixar rotulo="EPS" aoBaixar={() => void baixarImagem(pedido, "eps", nome)} />
            </span>
          ) : null
        }
      >
        {desenho && !gerando && (
          <span>
            gerado em <b className="num" style={{ color: "var(--txt)" }}>{desenho.segundos.toFixed(1)} s</b>
            {" · "}
            <span className="num">{Math.round(desenho.bytes / 1024)} kB</span>
          </span>
        )}
        <span style={{ color: "var(--txt3)" }}>
          O desenho nunca é reaproveitado: o que está na tela é o pinout de agora.
        </span>
      </BarraDeEstado>
    </div>
  )
}

/**
 * O desenho. Vai num `<img>` com blob em vez de embutido no HTML: SVG embutido
 * roda script, e um desenho não precisa desse direito.
 */


function Campo({ rotulo, largura, children }: { rotulo: string; largura: number; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, width: largura, maxWidth: "100%" }}>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--txt2)" }}>{rotulo}</span>
      {children}
    </label>
  )
}

function Lista({ rotulo, valor, opcoes, aoMudar, largura }: {
  rotulo: string; valor: number; opcoes: string[]; aoMudar: (v: number) => void; largura: number
}) {
  return (
    <Campo rotulo={rotulo} largura={largura}>
      <select
        className="campo"
        value={valor}
        onChange={(e) => aoMudar(Number(e.target.value))}
        aria-label={rotulo}
        style={{
          width: "100%", padding: "0 9px", borderRadius: 7,
          border: "1px solid var(--line2)", background: "var(--surface2)", fontSize: 12.5,
        }}
      >
        {opcoes.map((o, i) => <option key={o} value={i}>{o}</option>)}
      </select>
    </Campo>
  )
}

function Marca({ rotulo, ligado, aoMudar }: {
  rotulo: string; ligado: boolean; aoMudar: (v: boolean) => void
}) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 32, fontSize: 12.5, color: "var(--txt2)" }}>
      <input type="checkbox" checked={ligado} onChange={(e) => aoMudar(e.target.checked)} />
      {rotulo}
    </label>
  )
}

function Aviso({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
      fontSize: 13, color: "var(--txt2)", textAlign: "center",
    }}>
      {children}
    </div>
  )
}


function Passo({ rotulo, aoIr, texto }: { rotulo: string; aoIr: () => void; texto: string }) {
  return (
    <button
      onClick={aoIr}
      aria-label={rotulo}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 5, border: "1px solid var(--line2)",
        background: "var(--surface)", color: "var(--txt2)", fontSize: 13, fontWeight: 700,
      }}
    >
      {texto}
    </button>
  )
}
